import { useState } from 'react';

import {
  FreshStrawImplantDetail,
  useGetFreshStrawImplantDetailsQuery,
} from '@/app/services/freshStrawRest';

import type { FreshStrawSession } from '../../hooks/useFreshStrawSession';

interface FreshStrawViewImplantsTabProps {
  session: FreshStrawSession;
}

export function FreshStrawViewImplantsTab({
  session,
}: FreshStrawViewImplantsTabProps) {
  const [farmLocationName, setFarmLocationName] = useState('');

  const ready = !!session.transferSite && !!session.transferDate;

  const query = useGetFreshStrawImplantDetailsQuery(
    {
      transferDate: session.transferDate,
      transferSiteId: session.transferSite,
      accountId: session.accountId || undefined,
      freshShipmentId: session.freshShipmentId || undefined,
      farmLocationName: farmLocationName || undefined,
    },
    { skip: !ready },
  );

  const rows = (query.data ?? []) as FreshStrawImplantDetail[];

  const farmLocations = Array.from(
    new Set(rows.map((r) => r.farmLocationName).filter(Boolean) as string[]),
  ).sort();

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600">
            Farm location filter
          </label>
          <select
            value={farmLocationName}
            onChange={(e) => setFarmLocationName(e.target.value)}
            className="min-w-[220px] rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">All locations</option>
            {farmLocations.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </div>
        <div className="text-xs text-gray-500">
          Shows implants for the current transfer date / site / account /
          shipment from the filter panel.
        </div>
      </div>

      {!ready && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Set transfer site and transfer date in the filter panel to load
          implanted embryos.
        </div>
      )}

      {query.isFetching && (
        <div className="text-sm text-gray-500">Loading…</div>
      )}

      {query.isError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Failed to load implant details.
        </div>
      )}

      {ready && !query.isFetching && rows.length === 0 && (
        <div className="flex flex-1 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
          No implants match the current filters.
        </div>
      )}

      {rows.length > 0 && (
        <div className="min-h-0 flex-1 overflow-auto rounded border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-100 text-left text-xs font-semibold text-gray-600">
              <tr>
                <th className="px-2 py-1">Straw</th>
                <th className="px-2 py-1">Mating</th>
                <th className="px-2 py-1">Recip</th>
                <th className="px-2 py-1">Stage</th>
                <th className="px-2 py-1">Grade</th>
                <th className="px-2 py-1">Qual</th>
                <th className="px-2 py-1">Dist</th>
                <th className="px-2 py-1">CL</th>
                <th className="px-2 py-1">Tag</th>
                <th className="px-2 py-1">Tech</th>
                <th className="px-2 py-1">Location</th>
                <th className="px-2 py-1">DC ID</th>
                <th className="px-2 py-1">Comment</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.embryoId}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-2 py-1">{r.strawNumber ?? ''}</td>
                  <td className="px-2 py-1">{r.mating ?? ''}</td>
                  <td className="px-2 py-1">{r.recip ?? ''}</td>
                  <td className="px-2 py-1">{r.stage ?? ''}</td>
                  <td className="px-2 py-1">{r.grade ?? ''}</td>
                  <td className="px-2 py-1">{r.quality ?? ''}</td>
                  <td className="px-2 py-1">{r.distance ?? ''}</td>
                  <td className="px-2 py-1">{r.clLeftRight ?? ''}</td>
                  <td className="px-2 py-1">{r.earTag ?? ''}</td>
                  <td className="px-2 py-1">{r.transferTechName ?? ''}</td>
                  <td className="px-2 py-1">{r.farmLocationName ?? ''}</td>
                  <td className="px-2 py-1">{r.dairyCompId ?? ''}</td>
                  <td className="px-2 py-1">{r.transferComment ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
